import { createElement, type ReactElement } from 'react';
import type { DocumentProps } from '@react-pdf/renderer';
import type {
  RepositoryAnalysis,
  DriftReport,
  MaintenanceScoreResult,
} from '../../components/types/analysis.types';

function reportFileName(repositoryName: string): string {
  const safe = repositoryName.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
  const date = new Date().toISOString().split('T')[0];
  return `autar-sdlc-${safe}-${date}.pdf`;
}

// react-pdf's <Image> throws (aborting the whole render) if the source fails to
// load, so fetch the logo up front and fall back to a rendered monogram.
async function fetchLogoAsDataUrl(): Promise<string | null> {
  try {
    const response = await fetch('/Autar_logo_256x256.png');
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function triggerDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function generateAnalysisPDF(
  data: RepositoryAnalysis,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _jobId?: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _driftData?: DriftReport | null,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _maintenanceScore?: MaintenanceScoreResult | null,
): Promise<void> {
  const logoDataUrl = await fetchLogoAsDataUrl();

  // Lazy-load react-pdf so its engine stays out of the initial bundle.
  const { pdf } = await import('@react-pdf/renderer');
  const { OldReportDocument } = await import('./oldReport');

  const element = createElement(OldReportDocument, {
    data,
    logoDataUrl,
  }) as ReactElement<DocumentProps>;
  const blob = await pdf(element).toBlob();
  triggerDownload(blob, reportFileName(data.repositoryName));
}
